import { Upload, Text, Form, Modal, Button, Icon } from 'tea-component';
import { useState, useRef } from 'react';
import './index.scss';
import md5 from 'js-file-md5';
import moment from 'moment/moment';
import { checkNullString } from '@/pages/datalab/utils/utils';
import {getAuthorization} from "@/utils/utils";
export interface UploadFileProps {
  action: string;
  uploadEnd: (result: UploadFileResult) => void;
}

export interface UploadFileResult {
  md5: string | null;
  res: boolean;
}

function UploadFile(props: UploadFileProps) {
  const { action, uploadEnd, uploadFormatMessage } = props;
  const [uploadTime, setUploadTime] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [percent, setPercent] = useState(null);
  const fileMd5 = useRef('');

  const xhrRef = useRef(null);

  function handleStart(file: File, { xhr }: { xhr: XMLHttpRequest; }) {
    setFile(file);
    // getBase64(file);
    setStatus('validating');
    setUploadTime(moment().format('YYYY-MM-DD HH:mm:ss'));
    xhrRef.current = xhr;
  }

  function handleProgress({ percent }) {
    setPercent(Math.round(percent));
  }

  function handleSuccess(result) {
    setPercent(null);
    if (result.code === 0) {
      setStatus('success');
      uploadEnd({ res: true, md5: fileMd5.current });
    } else {
      setErrMsg(result.msg);
      setStatus('error');
      uploadEnd({ res: false, md5: fileMd5.current });
    }
  }

  function handleError() {
    setStatus('error');
    setPercent(null);
    uploadEnd({ res: false, md5: fileMd5.current });
  }

  const beforeUpload = (file, fileList, isAccepted) =>
    getBase64(file).then((value: any) => {
      fileMd5.current = value;
      return isAccepted;
    });

  const getBase64 = (file: File) =>
    new Promise((resolve) => {
      md5(file).then((val: string) => {
        resolve(val);
      });
    });

  function handleAbort() {
    if (xhrRef.current) {
      xhrRef.current.abort();
    }
    setFile(null);
    setStatus(null);
    setPercent(null);
    setUploadTime(null);
  }

  return (
    <Form.Control
      status={status}
      message={
        status === 'error' ? (
          checkNullString(errMsg) ? (
            <div style={{ color: '#E54444' }}>{uploadFormatMessage("invalid")}</div>
          ) : (
            errMsg
          )
        ) : (
          <div>{uploadFormatMessage("info")}</div>
        )
      }
    >
      <Upload
        action={action}
        accept={['text/plain', 'text/csv']}
        maxSize={1024 * 1024 * 60} // 60M
        data={(file) => ({ md5: fileMd5.current })}
        onStart={handleStart}
        onProgress={handleProgress}
        onSuccess={handleSuccess}
        onError={handleError}
        beforeUpload={beforeUpload}
        className="dt-upload"
        headers={{
          Authorization: getAuthorization(),
        }}
      >
        {({ open, isDragging }) => (
          <Upload.Dragger
            style={{ width: '100%' }}
            filename={
              file && (
                <div>
                  <span style={{ marginRight: '9px' }}>{file.name}</span>
                  {status === 'success' ? (
                    <Icon type="success" />
                  ) : status === 'error' ? (
                    <Icon type="error" />
                  ) : (
                    <Icon type="loading" />
                  )}
                  {percent && ` ${percent}%`}
                </div>
              )
            }
            description={
              file && (
                <div style={{ color: '#444' }}>
                  <p>{uploadFormatMessage("size")}{Math.floor(file.size / 1024)}K</p>
                  <p>{uploadFormatMessage("date")}{uploadTime != null ? uploadTime : ''}</p>
                </div>
              )
            }
            button={
              status === 'validating' ? (
                <Button type="link" onClick={handleAbort}>
                  {uploadFormatMessage("cancel")}
                </Button>
              ) : (
                <>
                  <Button type="link" onClick={open}>
                    {uploadFormatMessage("reupload")}
                  </Button>
                  <Button type="link" style={{ marginLeft: 8 }} onClick={handleAbort}>
                    {uploadFormatMessage("delete")}
                  </Button>
                </>
              )
            }
          >
            {isDragging ? uploadFormatMessage("release_mouse"): (
              <>
                <a onClick={open}>{uploadFormatMessage("click")}</a>
                <Text theme="weak">{uploadFormatMessage("drag")}</Text>
              </>
            )}
          </Upload.Dragger>
        )}
      </Upload>
    </Form.Control>
  );
}

export default UploadFile;
